import { Group, Stack, Text, Tooltip } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';

export type HeatmapDataItem = {
  date: string;
  times: {
    time: string;
    count: number;
  }[];
};

interface HeatmapProps {
  data: HeatmapDataItem[];
}

const colors = ['#F1EEFF', '#DED9FF', '#C8BFFF', '#A395FC', '#7C68FB'];

const Heatmap: React.FC<HeatmapProps> = ({ data }) => {
  const { t } = useTranslation();
  const hours = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

  const getColor = (value: number) => {
    const thresholds = [1, 5, 10, 20, 30];
    for (let i = 0; i < thresholds.length; i++) {
      if (value <= thresholds[i]) {
        return colors[i];
      }
    }
    return colors[colors.length - 1];
  };

  // 判断第二个参数的子项是否在第一个参数的时间范围里
  const filterTimesByHour = (hourRange: string, isoTimes: string[]): string[] => {
    const [datePart, timePart] = hourRange.split(' ');
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = dayStr ? parseInt(dayStr, 10) : null; // day may be undefined

    const [hourStr] = timePart.split(':');
    const hour = parseInt(hourStr, 10);

    // Filter the isoTimes based on the hour range
    const filteredTimes = isoTimes.filter((isoTime) => {
      const time = new Date(isoTime);
      const timeYear = time.getUTCFullYear();
      const timeMonth = time.getUTCMonth() + 1; // Months are 0-indexed
      const timeDay = time.getUTCDate();
      const timeHour = time.getUTCHours();

      if (day) {
        // If day is specified, check year, month, day, and hour
        return timeYear === year && timeMonth === month && timeDay === day && timeHour === hour;
      } else {
        // If day is not specified, only check year, month, and hour
        return timeYear === year && timeMonth === month && timeHour === hour;
      }
    });

    return filteredTimes;
  };

  const translateTime = (times: string) => {
    const date = new Date(times);

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed, hence +1
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <Stack>
      <div className="relative flex flex-col items-center overflow-x-auto pb-10">
        {/* X轴 */}
        <div className="flex mb-4 w-full">
          <div className="flex-none w-[60px]"></div>
          {data.map((item, idx) => (
            <div
              key={idx}
              className="flex-1 items-center justify-center text-xs text-gray-700 font-semibold text-center min-w-[100px] mr-2"
            >
              {item.date}
            </div>
          ))}
        </div>

        {/* 热度图主体 */}
        {hours.map((hour, idx) => {
          return (
            <section key={idx} className="flex w-full">
              <div className="flex-none w-[60px] text-center text-xs text-gray-700 font-semibold">
                {!(idx % 3) ? hour : ''}
              </div>
              {data.map((item) => {
                let valueTimes: string[] = [];
                let count = 0;
                item.times.map((itemTime) => {
                  // 遍历时间是否符合
                  const hasValueTimeArr = filterTimesByHour(`${item.date} ${hour}`, [itemTime.time]);
                  // 有符合的就计算数量
                  if (hasValueTimeArr.length) {
                    valueTimes = valueTimes.concat(hasValueTimeArr);
                    count += itemTime.count;
                  }
                });
                const timesDisplay = valueTimes.map((item) => translateTime(item)).join(' | ');
                return (
                  <Tooltip
                    key={`${item.date}-${hour}`}
                    label={`${timesDisplay} ${t('dashboard.table.count')}: ${count}`}
                  >
                    <div
                      className={`flex-1 h-[20px] min-w-[100px] mr-2 rounded-xs mb-2 ${
                        count === 0 ? 'bg-gray-100' : ''
                      }`}
                      style={{ backgroundColor: count === 0 ? '' : getColor(count) }}
                    ></div>
                  </Tooltip>
                );
              })}
            </section>
          );
        })}

        {/* 颜色说明 */}
        <div className="absolute bottom-3 right-0">
          <Group mt={10}>
            <Text size={'xs'} c="gray.4">
              {t('dashboard.heatmap.quietest')}
            </Text>
            {colors.map((item) => (
              <div key={item} className="w-4 h-2" style={{ backgroundColor: item }}></div>
            ))}
            <Text size={'xs'} c="gray.4">
              {t('dashboard.heatmap.busiest')}
            </Text>
          </Group>
        </div>
      </div>
    </Stack>
  );
};

export default Heatmap;
